import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { Loader, CheckCircle, XCircle, Mail, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cognitoAuth } from '../services/cognito';
import { theme } from '../styles/theme';
import { Button, Label, FormGroup } from '../components/ui';
import logoColor from '../assets/libben-smart.png';

const API_URL = process.env.REACT_APP_API_URL;

const AlexaLinkPage = () => {
  const { t } = useTranslation('alexaLink');
  const [searchParams] = useSearchParams();

  const code        = searchParams.get('code');
  const state       = searchParams.get('state');
  const redirectUri = searchParams.get('redirect_uri');

  const [step, setStep]         = useState('login'); // login | selecting | done | error
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [connectors, setConnectors] = useState([]);
  const [selectedConnector, setSelectedConnector] = useState('');
  const [tenantId, setTenantId] = useState('');

  // Após login, busca conectores Alexa do tenant
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await cognitoAuth.signIn(email, password);
      if (!result.success) { setError(result.error || t('loginError')); return; }

      const user = result.user;
      setTenantId(user.tenantId);

      // Busca conectores do tipo alexa
      const idToken = sessionStorage.getItem('idToken') || localStorage.getItem('idToken');
      const res = await fetch(`${API_URL}/connectors/${user.tenantId}`, {
        headers: { Authorization: `Bearer ${idToken}` }
      });
      const data = await res.json();
      const alexaConnectors = (data.connectors || []).filter(c => c.type === 'alexa');

      if (alexaConnectors.length === 0) { setError(t('noAlexaConnector')); return; }
      if (alexaConnectors.length === 1) {
        setSelectedConnector(alexaConnectors[0].connectorId);
        await doLink(user.tenantId, alexaConnectors[0].connectorId, idToken);
        return;
      }

      setConnectors(alexaConnectors);
      setStep('selecting');
    } catch {
      setError(t('loginError'));
    } finally {
      setLoading(false);
    }
  };

  const doLink = async (tid, connectorId, idToken) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/alexa/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          grant_type:  'authorization_code',
          code,
          tenantId:    tid,
          connectorId,
          client_id:     process.env.REACT_APP_ALEXA_LINK_CLIENT_ID,
          client_secret: process.env.REACT_APP_ALEXA_LINK_CLIENT_SECRET
        })
      });

      const data = await res.json();
      if (!res.ok) { setError(data.error || t('linkError')); setStep('error'); return; }

      // Redireciona de volta para a Alexa com o code original + state
      const finalUrl = `${redirectUri}?code=${encodeURIComponent(data.access_token)}&state=${encodeURIComponent(state || '')}`;
      setStep('done');
      setTimeout(() => { window.location.href = finalUrl; }, 2000);
    } catch {
      setError(t('linkError'));
      setStep('error');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectConnector = async (e) => {
    e.preventDefault();
    if (!selectedConnector) return;
    const idToken = sessionStorage.getItem('idToken') || localStorage.getItem('idToken');
    await doLink(tenantId, selectedConnector, idToken);
  };

  return (
    <Wrapper>
      <Glow />
      <Card>
        <LogoArea>
          <Logo src={logoColor} alt="Libben Agents" />
          <AlexaBadge>🔗 {t('alexaLinking')}</AlexaBadge>
        </LogoArea>

        {step === 'done' && (
          <StatusBox success>
            <CheckCircle size={40} color={theme.colors.success} />
            <StatusTitle>{t('successTitle')}</StatusTitle>
            <StatusDesc>{t('successDesc')}</StatusDesc>
          </StatusBox>
        )}

        {step === 'error' && (
          <StatusBox>
            <XCircle size={40} color={theme.colors.danger} />
            <StatusTitle>{t('errorTitle')}</StatusTitle>
            <StatusDesc>{error}</StatusDesc>
          </StatusBox>
        )}

        {step === 'login' && (
          <form onSubmit={handleLogin}>
            <Hint>{t('loginHint')}</Hint>
            <FormGroup>
              <Label>{t('email')}</Label>
              <InputWrapper>
                <InputIcon><Mail size={16} /></InputIcon>
                <StyledInput
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder={t('emailPlaceholder')}
                  required
                  autoComplete="email"
                />
              </InputWrapper>
            </FormGroup>
            <FormGroup style={{ marginTop: theme.spacing[3] }}>
              <Label>{t('password')}</Label>
              <InputWrapper>
                <InputIcon><Lock size={16} /></InputIcon>
                <StyledInput
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                />
              </InputWrapper>
            </FormGroup>
            {error && <ErrorBox>{error}</ErrorBox>}
            <Button type="submit" style={{ width: '100%', marginTop: theme.spacing[5] }} disabled={loading}>
              {loading ? <Loader size={16} className="spin" /> : t('linkButton')}
            </Button>
          </form>
        )}

        {step === 'selecting' && (
          <form onSubmit={handleSelectConnector}>
            <Hint>{t('selectConnectorHint')}</Hint>
            <FormGroup style={{ marginTop: theme.spacing[3] }}>
              <Label>{t('selectConnector')}</Label>
              <StyledSelect value={selectedConnector} onChange={e => setSelectedConnector(e.target.value)} required>
                <option value="">{t('selectPlaceholder')}</option>
                {connectors.map(c => (
                  <option key={c.connectorId} value={c.connectorId}>{c.name}</option>
                ))}
              </StyledSelect>
            </FormGroup>
            {error && <ErrorBox>{error}</ErrorBox>}
            <Button type="submit" style={{ width: '100%', marginTop: theme.spacing[5] }} disabled={loading || !selectedConnector}>
              {loading ? <Loader size={16} className="spin" /> : t('confirmButton')}
            </Button>
          </form>
        )}
      </Card>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  min-height: 100vh;
  background: ${theme.colors.gray900};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[4]};
  position: relative;
  overflow: hidden;
`;

const Glow = styled.div`
  position: absolute;
  width: 600px;
  height: 600px;
  border-radius: 50%;
  background: radial-gradient(circle, ${theme.colors.primary}33 0%, transparent 70%);
  top: -200px;
  left: 50%;
  transform: translateX(-50%);
  pointer-events: none;
`;

const Card = styled.div`
  background: ${theme.colors.white};
  border-radius: ${theme.radii['2xl']};
  padding: ${theme.spacing[10]};
  width: 100%;
  max-width: 420px;
  box-shadow: ${theme.shadows.xl};
  position: relative;
  @media (max-width: 639px) {
    padding: ${theme.spacing[6]};
    border-radius: ${theme.radii.xl};
  }
`;

const LogoArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: ${theme.spacing[6]};
`;

const Logo = styled.img`
  height: 48px;
  width: auto;
  object-fit: contain;
  margin-bottom: ${theme.spacing[2]};
`;

const AlexaBadge = styled.div`
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textMuted};
  background: ${theme.colors.gray50};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.full};
  padding: 2px ${theme.spacing[3]};
`;

const Hint = styled.p`
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textSecondary};
  text-align: center;
  margin: 0 0 ${theme.spacing[4]};
  line-height: 1.5;
`;

const InputWrapper = styled.div`position: relative;`;

const InputIcon = styled.div`
  position: absolute;
  left: ${theme.spacing[3]};
  top: 50%;
  transform: translateY(-50%);
  color: ${theme.colors.textMuted};
  pointer-events: none;
  display: flex;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: ${theme.spacing[3]} ${theme.spacing[3]} ${theme.spacing[3]} ${theme.spacing[10]};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textPrimary};
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  outline: none;
  &::placeholder { color: ${theme.colors.textMuted}; }
  &:focus { border-color: ${theme.colors.primary}; box-shadow: 0 0 0 3px ${theme.colors.primaryLight}; }
`;

const StyledSelect = styled.select`
  width: 100%;
  padding: ${theme.spacing[3]};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textPrimary};
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  outline: none;
  &:focus { border-color: ${theme.colors.primary}; box-shadow: 0 0 0 3px ${theme.colors.primaryLight}; }
`;

const ErrorBox = styled.div`
  margin-top: ${theme.spacing[3]};
  padding: ${theme.spacing[3]};
  background: ${theme.colors.dangerLight};
  border: 1px solid ${theme.colors.dangerBorder};
  border-radius: ${theme.radii.md};
  color: #991b1b;
  font-size: ${theme.typography.sm};
  text-align: center;
`;

const StatusBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing[3]};
  text-align: center;
  padding: ${theme.spacing[4]} 0;
`;

const StatusTitle = styled.div`
  font-size: ${theme.typography.lg};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary};
`;

const StatusDesc = styled.div`
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textSecondary};
  line-height: 1.5;
`;

export default AlexaLinkPage;
